import { z } from 'zod';

/**
 * Avatar cache status for tracking cached avatar state
 */
export type AvatarCacheStatus = 'pending' | 'cached' | 'failed';

/**
 * Employee data interface with cached avatar support
 * This extends the existing employee_data structure to support avatar caching
 */
export interface EmployeeData {
  // Basic employee information
  id?: string | number;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  status?: string;
  role_name?: string;
  position?: string;
  team_name?: string;
  department?: string;
  manager_level?: string;
  is_admin?: string | boolean;
  hire_date?: { value: string } | Date;
  /** First-ever stint start from BambooHR via user_pivot; hire_date remains the current-stint anchor (rehires differ). */
  employee_start?: { value: string } | Date | null;
  /** Employment end date from BambooHR via user_pivot; null/absent while employed. Can be future-dated for known departures. */
  employee_end?: { value: string } | Date | null;
  bamboo_id?: string;

  // Avatar fields - backward compatible
  avatar?: string; // Original Google User Content URL (maintained for backward compatibility)
  
  // New cached avatar fields
  cached_avatar_url?: string; // Firebase Storage URL for primary avatar
  cached_thumbnail_url?: string; // Firebase Storage URL for thumbnail
  avatar_cache_timestamp?: Date; // When the avatar was last cached
  avatar_cache_status?: AvatarCacheStatus; // Cache status
  
  // Additional HRIS fields
  team_id?: string | number | null;
  division_id?: string | number | null;
  role_id?: string | number;
  m1_id?: string | number | null;
  m2_id?: string | number | null;
  manager_user_id?: string | number | null;
}

/**
 * System User interface for the system_users collection
 * Document ID: Employee ID (string representation)
 */
export interface SystemUser {
  // Identity
  id: string; // employee_id as string (document ID)
  employee_id: number; // Employee ID from user_pivot_sync
  firebase_uid?: string; // Optional link to Firebase Auth
  email: string; // Email from user_pivot_sync (cached)
  
  // Page-level permissions (managed in Admin)
  permissions: {
    canAdmin: boolean; // Access Admin pages
    canSetup: boolean; // Access Setup pages
    canReview: boolean; // Access Review pages
    canCollaborate: boolean; // Access Collaboration pages
    canViewSensitiveFinanceData: boolean; // Access Income and Forecasting pages
  };
  
  // Role (stored on some users, computed from permissions for others)
  role?: string;

  // Group-based access control (new system)
  groups?: string[];
  isSuperAdmin?: boolean;

  // Integration IDs
  slack_user_id?: string;
  slack_synced_at?: Date;
  asana_user_gid?: string;
  asana_synced_at?: Date;

  // Workflow/Program assignments (managed in Setup)
  assigned_workflows: number[]; // Workflow IDs user can access
  program_memberships: string[]; // Program IDs user belongs to
  starred_workflows?: string[];
  starred_users?: string[];
  starred_teams?: string[];

  // Employee data with cached avatar support
  employee_data?: EmployeeData;

  // CRM saved views
  crm_saved_view?: { filters: any[]; savedAt: Date };
  crm_saved_view_people?: { filters: any[]; savedAt: Date };

  // Data collection completion tracking
  data_collection_complete?: boolean; // User has clicked "Submit All Data" in MyTasks
  data_collection_completed_at?: Date; // When user marked as complete

  // Association tracking
  association_status: 'pending' | 'associated' | 'manual' | 'new_user_pending';

  // Lifecycle
  created_at: Date;
  updated_at: Date;
  created_by?: string; // Admin UID who created
  updated_by?: string;
}

/**
 * User Profile interface with cached avatar support
 * This extends the existing UserProfile to include cached avatar fields
 */
export interface SystemUserProfile {
  uid: string;
  employee_id: number;
  email: string;
  name: string;
  team_name: string;
  role_name: string;
  role: 'admin' | 'manager' | 'contributor';
  lastActive: Date;
  
  // Avatar fields - backward compatible
  avatar?: string; // Original avatar URL (maintained for backward compatibility)
  
  // New cached avatar fields
  cached_avatar_url?: string; // Firebase Storage URL for primary avatar
  cached_thumbnail_url?: string; // Firebase Storage URL for thumbnail
  avatar_cache_timestamp?: Date; // When the avatar was last cached
  avatar_cache_status?: AvatarCacheStatus; // Cache status
}

/**
 * Create/Update interfaces for system users
 */
export interface CreateSystemUserData {
  employee_id: number;
  firebase_uid?: string;
  email: string;
  permissions?: Partial<SystemUser['permissions']>;
  assigned_workflows?: number[];
  program_memberships?: string[];
  employee_data?: Partial<EmployeeData>;
  association_status?: SystemUser['association_status'];
  created_by?: string;
}

export interface UpdateSystemUserData {
  firebase_uid?: string;
  email?: string;
  permissions?: Partial<SystemUser['permissions']>;
  assigned_workflows?: number[];
  program_memberships?: string[];
  employee_data?: Partial<EmployeeData>;
  association_status?: SystemUser['association_status'];
  data_collection_complete?: boolean;
  data_collection_completed_at?: Date;
}

// Zod schemas for validation

/**
 * Schema for avatar cache status
 */
export const AvatarCacheStatusSchema = z.enum(['pending', 'cached', 'failed']);

/**
 * Schema for employee data with cached avatar support
 */
export const EmployeeDataSchema = z.object({
  // Basic employee information
  id: z.union([z.string(), z.number()]).optional(),
  name: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  email: z.string().email().optional(),
  status: z.string().optional(),
  role_name: z.string().optional(),
  position: z.string().optional(),
  team_name: z.string().optional(),
  department: z.string().optional(),
  manager_level: z.string().optional(),
  is_admin: z.union([z.string(), z.boolean()]).optional(),
  hire_date: z.union([
    z.object({ value: z.string() }),
    z.date()
  ]).optional(),
  employee_start: z.union([
    z.object({ value: z.string() }),
    z.date(),
    z.null()
  ]).optional(),
  employee_end: z.union([
    z.object({ value: z.string() }),
    z.date(),
    z.null()
  ]).optional(),
  bamboo_id: z.string().optional(),
  
  // Avatar fields - backward compatible
  avatar: z.string().url().optional(),
  
  // New cached avatar fields
  cached_avatar_url: z.string().url().optional(),
  cached_thumbnail_url: z.string().url().optional(),
  avatar_cache_timestamp: z.date().optional(),
  avatar_cache_status: AvatarCacheStatusSchema.optional(),
  
  // Additional HRIS fields
  team_id: z.union([z.string(), z.number(), z.null()]).optional(),
  division_id: z.union([z.string(), z.number(), z.null()]).optional(),
  role_id: z.union([z.string(), z.number()]).optional(),
  m1_id: z.union([z.string(), z.number(), z.null()]).optional(),
  m2_id: z.union([z.string(), z.number(), z.null()]).optional(),
  manager_user_id: z.union([z.string(), z.number(), z.null()]).optional(),
});

/**
 * Schema for system user permissions
 */
export const SystemUserPermissionsSchema = z.object({
  canAdmin: z.boolean(),
  canSetup: z.boolean(),
  canReview: z.boolean(),
  canCollaborate: z.boolean(),
  canViewSensitiveFinanceData: z.boolean().default(false),
});

/**
 * Schema for system user
 */
export const SystemUserSchema = z.object({
  id: z.string(),
  employee_id: z.number(),
  firebase_uid: z.string().optional(),
  email: z.string().email(),
  permissions: SystemUserPermissionsSchema,
  role: z.string().optional(),
  groups: z.array(z.string()).optional(),
  isSuperAdmin: z.boolean().optional(),
  slack_user_id: z.string().optional(),
  asana_user_gid: z.string().optional(),
  assigned_workflows: z.array(z.number()),
  program_memberships: z.array(z.string()),
  starred_workflows: z.array(z.string()).optional(),
  starred_users: z.array(z.string()).optional(),
  starred_teams: z.array(z.string()).optional(),
  employee_data: EmployeeDataSchema.optional(),
  crm_saved_view: z.object({ filters: z.array(z.any()), savedAt: z.date() }).optional(),
  crm_saved_view_people: z.object({ filters: z.array(z.any()), savedAt: z.date() }).optional(),
  data_collection_complete: z.boolean().optional(),
  data_collection_completed_at: z.date().optional(),
  association_status: z.enum(['pending', 'associated', 'manual', 'new_user_pending']),
  created_at: z.date(),
  updated_at: z.date(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
});

/**
 * Schema for system user profile with cached avatar support
 */
export const SystemUserProfileSchema = z.object({
  uid: z.string(),
  employee_id: z.number(),
  email: z.string().email(),
  name: z.string(),
  team_name: z.string(),
  role_name: z.string(),
  role: z.enum(['admin', 'manager', 'contributor']),
  lastActive: z.date(),
  
  // Avatar fields - backward compatible
  avatar: z.string().url().optional(),
  
  // New cached avatar fields
  cached_avatar_url: z.string().url().optional(),
  cached_thumbnail_url: z.string().url().optional(),
  avatar_cache_timestamp: z.date().optional(),
  avatar_cache_status: AvatarCacheStatusSchema.optional(),
});

/**
 * Schema for creating system user data
 */
export const CreateSystemUserDataSchema = z.object({
  employee_id: z.number(),
  firebase_uid: z.string().optional(),
  email: z.string().email(),
  permissions: SystemUserPermissionsSchema.partial().optional(),
  assigned_workflows: z.array(z.number()).optional().default([]),
  program_memberships: z.array(z.string()).optional().default([]),
  employee_data: EmployeeDataSchema.partial().optional(),
  association_status: z.enum(['pending', 'associated', 'manual', 'new_user_pending']).optional().default('pending'),
  created_by: z.string().optional(),
});

/**
 * Schema for updating system user data
 */
export const UpdateSystemUserDataSchema = z.object({
  firebase_uid: z.string().optional(),
  email: z.string().email().optional(),
  permissions: SystemUserPermissionsSchema.partial().optional(),
  assigned_workflows: z.array(z.number()).optional(),
  program_memberships: z.array(z.string()).optional(),
  employee_data: EmployeeDataSchema.partial().optional(),
  association_status: z.enum(['pending', 'associated', 'manual', 'new_user_pending']).optional(),
  data_collection_complete: z.boolean().optional(),
  data_collection_completed_at: z.date().optional(),
});

/**
 * Type guards for system user data
 */
export const isValidSystemUser = (data: unknown): data is SystemUser => {
  return SystemUserSchema.safeParse(data).success;
};

export const isValidEmployeeData = (data: unknown): data is EmployeeData => {
  return EmployeeDataSchema.safeParse(data).success;
};

export const isValidSystemUserProfile = (data: unknown): data is SystemUserProfile => {
  return SystemUserProfileSchema.safeParse(data).success;
};

export const isValidAvatarCacheStatus = (status: unknown): status is AvatarCacheStatus => {
  return AvatarCacheStatusSchema.safeParse(status).success;
};

/**
 * Helper functions for avatar cache management
 */
export const avatarCacheHelpers = {
  /**
   * Helper function to convert Firestore Timestamp to Date
   */
  convertTimestamp: (timestamp: any): Date | undefined => {
    if (!timestamp) return undefined;
    
    // If it's already a Date object, return it
    if (timestamp instanceof Date) return timestamp;
    
    // If it's a Firestore Timestamp, convert it
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // If it's a timestamp object with seconds/nanoseconds
    if (timestamp && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
    }
    
    // Try to parse as date string
    if (typeof timestamp === 'string') {
      const parsed = new Date(timestamp);
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
    
    return undefined;
  },

  /**
   * Check if avatar cache is valid (not expired)
   */
  isCacheValid: (timestamp?: Date | any, maxAgeHours: number = 24): boolean => {
    const convertedTimestamp = avatarCacheHelpers.convertTimestamp(timestamp);
    if (!convertedTimestamp) return false;
    const now = new Date();
    const ageHours = (now.getTime() - convertedTimestamp.getTime()) / (1000 * 60 * 60);
    return ageHours < maxAgeHours;
  },

  /**
   * Get the best available cached avatar URL (Firebase Storage only)
   */
  getBestAvatarUrl: (employeeData?: EmployeeData): string | undefined => {
    if (!employeeData) return undefined;
    
    // Only return cached avatar if available and status is 'cached'
    if (employeeData.cached_avatar_url && employeeData.avatar_cache_status === 'cached') {
      return employeeData.cached_avatar_url;
    }
    
    // No fallback to original avatar - show initials instead
    return undefined;
  },

  /**
   * Get thumbnail URL if available
   */
  getThumbnailUrl: (employeeData?: EmployeeData): string | undefined => {
    if (!employeeData) return undefined;
    
    // Return thumbnail if available and status is 'cached'
    if (employeeData.cached_thumbnail_url && employeeData.avatar_cache_status === 'cached') {
      return employeeData.cached_thumbnail_url;
    }
    
    return undefined;
  },

  /**
   * Check if avatar needs caching (no cached version or cache is stale)
   * Note: This is primarily for migration/admin purposes since we no longer rely on original avatars
   */
  needsCaching: (employeeData?: EmployeeData, maxAgeHours: number = 24): boolean => {
    if (!employeeData?.avatar) return false; // No original avatar to cache
    
    // If no cached version exists
    if (!employeeData.cached_avatar_url || employeeData.avatar_cache_status !== 'cached') {
      return true;
    }
    
    // If cache is stale
    return !avatarCacheHelpers.isCacheValid(employeeData.avatar_cache_timestamp, maxAgeHours);
  }
};